import React, { useState, useCallback, useRef } from 'react';
import { useGlobalCanvas } from '../contexts/GlobalCanvasContext';

interface EnterpriseTabProps {
  tab: any;
  index: number;
  viewId: string;
  isActive: boolean;
  onSelect: () => void;
  onClose: () => void;
  onDragStart: (e: React.DragEvent, index: number) => void;
  onDragEnter: (e: React.DragEvent, index: number) => void;
  onDragEnd: () => void;
  onDrop: (e: React.DragEvent, index: number) => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

const EnterpriseTab: React.FC<EnterpriseTabProps> = ({
  tab,
  index,
  isActive,
  onSelect,
  onClose,
  onDragStart,
  onDragEnter,
  onDragEnd,
  onDrop,
  onContextMenu
}) => {
  const [isDragging, setIsDragging] = useState(false);

  const getTabIcon = (type: string): string => {
    const iconMap: Record<string, string> = {
      'task': 'task_alt',
      'email': 'mail',
      'opportunity': 'monetization_on',
      'deal': 'monetization_on',
      'person': 'person',
      'client': 'person',
      'file': 'description',
      'workflow': 'sync',
      'chat': 'chat',
      'canvas': 'dashboard',
      'clients-list': 'people',
      'deals-list': 'handshake',
      'tasks-list': 'checklist',
      'emails-list': 'inbox',
      'default': 'tab'
    };
    return iconMap[type] || 'tab';
  };

  const handleDragStart = (e: React.DragEvent) => {
    setIsDragging(true);
    onDragStart(e, index);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    onDragEnd();
  };

  const handleMiddleClick = (e: React.MouseEvent) => {
    if (e.button === 1) { // Middle mouse button
      e.preventDefault();
      if (tab.canClose !== false) {
        onClose();
      }
    }
  };

  return (
    <div
      className={`enterprise-tab ${isActive ? 'active' : ''} ${isDragging ? 'dragging' : ''}`}
      onClick={onSelect}
      onMouseDown={handleMiddleClick}
      onContextMenu={onContextMenu}
      draggable={true}
      onDragStart={handleDragStart}
      onDragEnter={(e) => onDragEnter(e, index)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => onDrop(e, index)}
      onDragEnd={handleDragEnd}
      role="tab"
      aria-selected={isActive}
      aria-label={tab.title}
    >
      <span 
        className="material-symbols-outlined tab-icon" 
        data-type={tab.type}
        aria-hidden="true"
      >
        {getTabIcon(tab.type)}
      </span>

      <span className="tab-title">{tab.title}</span>

      <div className="tab-actions">
        {tab.isPinned && (
          <span className="tab-pin" title="Pinned" aria-label="Pinned tab">
            📌
          </span>
        )}
        
        {tab.canClose !== false && (
          <button
            className="tab-close"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            title="Close tab"
            aria-label="Close tab"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

interface EnhancedTabBarProps {
  view: any;
}

const EnhancedTabBar: React.FC<EnhancedTabBarProps> = ({ view }) => {
  const {
    setActiveTab,
    closeTab,
    splitTabToNewView,
    reorderTabs,
    moveTabBetweenViews,
    duplicateTab
  } = useGlobalCanvas();

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const dropIndicatorRef = useRef<HTMLDivElement>(null);

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', view.tabs[index].id);
    e.dataTransfer.setData('sourceViewId', view.id);
  }, [view]);

  const handleDragEnter = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  }, [draggedIndex]);

  const handleDrop = useCallback((e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    const sourceViewId = e.dataTransfer.getData('sourceViewId');
    const tabId = e.dataTransfer.getData('text/plain');

    if (sourceViewId === view.id) {
      // Reorder within same view
      if (draggedIndex !== null && draggedIndex !== targetIndex) {
        reorderTabs(view.id, draggedIndex, targetIndex);
      }
    } else {
      // Move between views
      moveTabBetweenViews(tabId, sourceViewId, view.id, targetIndex);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [view.id, draggedIndex, reorderTabs, moveTabBetweenViews]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  const handleContextMenu = useCallback((e: React.MouseEvent, tabId: string) => {
    e.preventDefault();
    // Context menu functionality temporarily removed
  }, []);

  return (
    <>
      <div className="enterprise-tab-bar">
        <div className="tab-scroll-area">
          {view.tabs.map((tab: any, index: number) => (
            <EnterpriseTab
              key={tab.id}
              tab={tab}
              index={index}
              viewId={view.id}
              isActive={tab.id === view.activeTabId}
              onSelect={() => setActiveTab(tab.id)}
              onClose={() => closeTab(tab.id)}
              onDragStart={handleDragStart}
              onDragEnter={handleDragEnter}
              onDragEnd={handleDragEnd}
              onDrop={handleDrop}
              onContextMenu={(e) => handleContextMenu(e, tab.id)}
            />
          ))}
        </div>

        {/* Split View Controls */}
        {view.tabs.length > 0 && (
          <div className="tab-bar-actions">
            <button
              className="tab-bar-action-btn"
              onClick={() => {
                const activeTab = view.tabs.find((t: any) => t.id === view.activeTabId);
                if (activeTab) {
                  splitTabToNewView(activeTab.id, 'horizontal');
                }
              }}
              title="Split Right"
              aria-label="Split view right"
            >
              <span className="material-symbols-outlined">view_column</span>
            </button>
            <button
              className="tab-bar-action-btn"
              onClick={() => {
                const activeTab = view.tabs.find((t: any) => t.id === view.activeTabId);
                if (activeTab) {
                  splitTabToNewView(activeTab.id, 'vertical');
                }
              }}
              title="Split Down"
              aria-label="Split view down"
            >
              <span className="material-symbols-outlined">horizontal_split</span>
            </button>
          </div>
        )}

        {/* Drop indicator */}
        {dragOverIndex !== null && (
          <div
            ref={dropIndicatorRef}
            className="tab-drop-indicator"
            style={{
              left: `${dragOverIndex * 130 + (dragOverIndex > (draggedIndex || 0) ? 130 : 0)}px`
            }}
          />
        )}
      </div>
    </>
  );
};

export default EnhancedTabBar;